import {
	A,
	all,
	ElementRenderer,
	Button,
	Checkbox,
	Div,
	Input,
	Label,
	Mark,
	Form,
	reactive,
	not,
	Search,
	Span,
	TextArea,
	Variable,
} from 'alkali'
import { get, post } from 'tools/request'
import Icon from 'components/Icon'
import * as searchIcon from 'icons/search.svg'
import { suggestions, liveParsed, liveQuestion, liveStructuredQuery, structuredQuery, executeSearch, parseQuestion,
	faqOrReadOnly, docSearchQuery, updateLocationForSearch, studyFilter, currentAnalysisSelections, analysisSelections } from 'model/SearchQuery'
import { SuggestionDropdown } from './SuggestionDropdown'
import { startExplanations, showingExplanation } from 'components/entry/Explanation.ts'
import { renderQuestion, renderCurrentQuestionInto } from './Question'
import { decodeLabel } from 'analysis/Question'
import { FavoriteControl } from '../navbar/FavoriteControl'
import './QuestionBar.scss'
import { app, userSettings } from 'app/app'
import { cloneDeep } from 'lodash'
const showSuggestions = reactive(false)
import { Chip } from 'components/generic/Chip'
import { friendlyCollectionName } from '../filter/ImportCollections'
import { track } from 'components/tracking/track'

let hasFocus
let suggestionDropdown
let hasShownEditingWarning
const questionEditing = userSettings.to(settings =>
	(typeof settings.QuestionEditing == 'boolean' ? settings.QuestionEditing : app.user.IsInternal) && !faqOrReadOnly.valueOf())
export let hasInputed
let missingAttributes = liveStructuredQuery.to(({ interventions, characteristics}) => interventions.length == 0 && characteristics.length == 0)

export default class QuestionBar extends Form('.question-bar.has-searched', {
	classes: {
		'readonly-mode': faqOrReadOnly,
		'editing-mode': questionEditing,
		'show-suggestions': showSuggestions,
		'has-collection-query': docSearchQuery.to(query => query?.query ? true :
			studyFilter.importCollection.to(Boolean)),
	},
	onsubmit(event) {
		event.preventDefault()
		this.doSearch()
	},
}) {
	articleSearch: Variable
	evidenceSearch: Variable
	annotatedPICO: Variable
	searchURIs: Variable
	whenAnnotated: Promise
	searchInput: Div
	highlighted: Div
	annotations: any
	lastQuery: string
	asQuestion: string
	injectedInput = true
	queuedChange: NodeJS.Timeout
	ready(properties) {
		let buttonId = properties.buttonId ?? ''
		let searchForm = this
		setTimeout(() => (this.injectedInput = false))
		window.addEventListener('resize', () => {
			this.searchInput.adjustHeight()
		})
		this.append([
			Div('.backdrop', [
				(this.highlighted = new Div('.highlights')),
				FavoriteControl,
			]),
			(this.searchInput = new TextArea('.main-search', {
				// the search input, with as contentEditable div so we can color code words in it
				spellcheck: false, // medical words are often not in browser dictionaries
				placeholder: 'Ask how a treatment will affect a condition...',
				readOnly: not(questionEditing),
				oninput: (event) => {
					// TODO: Use the suggestion API to basically just spell check the last word in the text input
					/*get('https://searchbeta.doctorevidence.com/api/dictionary/suggestions?query=' +
						encodeURIComponent(event.target.value) +
						'&limit=10', {noHeaders: true, headers: {}}).then(suggestionResponse => {
							topSuggestionUris = suggestionResponse['dict/suggestions'][0]['suggestion/uris']?
							topSuggestionTerm = suggestionResponse['dict/suggestions'][0]['suggestion/key']?
						})*/
					this.searchInput.adjustHeight()
					if (this.injectedInput) return
					hasInputed = true
					this.inputChange()
				},
				onkeydown: (event) => {
					if (event.keyCode == 13) {
						event.preventDefault()
						let annotation = this.findPlaceholderPosition()
						if (annotation) {
							alert('Please fill in question placeholders')
							this.searchInput.setSelectionRange(annotation.start, annotation.end)
						} else {
							this.newSearch(
								!showSuggestions.valueOf() || suggestionDropdown.currentIndex == -1
							)
							showSuggestions.put(false)
						}
					} else if (event.keyCode == 9) {
						let annotation = this.findAnnotationPosition(this.searchInput.selectionEnd, true)
						if (annotation) {
							event.preventDefault()
							this.searchInput.setSelectionRange(annotation.start, annotation.end)
						}
					} else if (event.keyCode == 27) {
						// escape closes suggestions
						showSuggestions.put(false)
					}
					if (showSuggestions.valueOf()) {
						if (event.keyCode == 40) {
							event.preventDefault()
							suggestionDropdown.cursorDown()
						} else if (event.keyCode == 38) {
							event.preventDefault()
							suggestionDropdown.cursorUp()
						}
					}
				},
				onclick: (event) => {
					//this.visibilityTimeout = setTimeout(() => {
					if (!questionEditing.valueOf()) {
						if (!faqOrReadOnly.valueOf()) {
							if (!hasShownEditingWarning) {
								hasShownEditingWarning = true
								// TODO: Replace with new tour navigation
								// startExplanations(7, true, false, {}, this.searchInput)
							}
						}
						return 
					}
					let wasRange = this.searchInput.selectionStart < this.searchInput.selectionEnd
					setTimeout(() => searchForm.selectAndShowSuggestions(wasRange))
					//}, 40)
				},
				getDefaultTitle() {
					return questionEditing.valueOf()
						? 'Ask how a treatment will affect a condition...'
						: (`This shows the current medical question you are asking.` + (faqOrReadOnly.valueOf() ? '' : `
If you want to ask your research question using this search bar, enable natural language settings for the search bar, in the App Settings.
Reminder: DocAnalytics is currently optimized to answer safety and efficacy questions for therapies, using one of the provided templates.
To unlock advanced user settings for the search bar, see App Settings.`))
				},
				onmousemove(event) {
					// all the title/tooltips for the annotations are "hidden" behind the textarea, so we track mouse movements
					// to "transfer" the title of the annotation that is behind the cursor up to the textarea
					let x = event.offsetX
					let y = event.offsetY
					for (let mark of searchForm.highlighted.querySelectorAll('mark')) {
						let markX = mark.offsetLeft - 10
						let markY = mark.offsetTop - 10
						if (
							markX <= x &&
							markX + mark.offsetWidth + 20 >= x &&
							markY <= y &&
							markY + mark.offsetHeight + 20 >= y
						) {
							this.title = mark.title
							return
						}
					}
					this.title = this.getDefaultTitle()
				},
				onmouseout(event) {
					this.title = this.getDefaultTitle()
				},
				onfocus(event) {
					clearTimeout(this.visibilityTimeout)
					searchForm.classList.remove('is-blurred')
					this.visibilityTimeout = setTimeout(() => {
						hasFocus = true
						if (this.hasBlurred && app.user.Settings?.QuestionEditing) showSuggestions.put(true)
					}, 10)
				},
				onblur(event) {
					clearTimeout(this.visibilityTimeout)
					searchForm.classList.add('is-blurred')
					this.hasBlurred = true
					this.visibilityTimeout = setTimeout(() => {
						hasFocus = false
						showSuggestions.put(false)
					}, 10)
				},
				adjustHeight() {
					let scrollHeight = this.scrollHeight
					if (scrollHeight <= this.clientHeight) {
						this.style.transition = ''
						this.style.height = this.offsetHeight - 1.2 + 'px'
						this.scrollTop = 0
						requestAnimationFrame(() => this.adjustHeight())
					} else if (scrollHeight > this.clientHeight - 10) {
						this.style.transition = 'height 0.4s'
						this.style.height = scrollHeight + 1 + 'px'
						requestAnimationFrame(() => (this.scrollTop = 0))
					}
				},
			})),
			docSearchQuery.to(async (query) =>
				query?.query
					? new Chip('.ds-query', {
									icon: 'DS',
									name: query.name,
									docSearchLink: (await app.user)?.Platforms?.some(p => p.Name == 'DOC Search') && query.query,
									onClose: () => {
										if (confirm('Are you sure you want to remove the DOC Search query?')) {
											docSearchQuery.put({})
											updateLocationForSearch()
										}
									},
					    }) :
					studyFilter.importCollection.to(collection => {
						return collection ?
						new Chip('.collection-query', {
							icon: '🗍',
							name: friendlyCollectionName(collection),
							onClose: () => {
								studyFilter.set('importCollection', null)
							},
						}) : null})
			),
			// COMMENTED OUT FOR NOW - Keeping just in case tagging is moved back to Questionbar
			// Delete this if fix version > 1.1
			// isFAQDashboard ? currentAnalysisSelections.to(({tags}) => {
			// 	if(tags){
			// 			return new Div('.analysis-tags',
			// 				tags.map(tag => {
			// 					return Div('.analysis-tag', [
			// 						Span('.tag-name.truncate', {content: tag, title: tag}),
			// 					])
			// 				}),
			// 			)
			// 	}
			// }) : null,
			faqOrReadOnly.valueOf() ? Icon('.execute-search', {
				icon: searchIcon,
				title:
					'Asks this question, reseting the filters, selections, and analysis using this question'
			}) : null,
			(suggestionDropdown = new SuggestionDropdown({
				suggestions,
				searchTarget: this,
			})),
			isFAQDashboard ? null : Button(`.execute-search#${buttonId}`, {
				content: Icon('.search-icon', {
					icon: searchIcon,
					title: 'Asks this question, reseting the filters, selections, and analysis using this question'
				}),
				type:'button',
				disabled: missingAttributes,
				title: missingAttributes.to(missingAttributes => missingAttributes ? 'Please provide at least one treatment or condition before beginning query and analysis' : 'Click to ask question, search for studies, and begin analysis'),
				onclick: () => {
					this.newSearch()
					track({ event: 'ask-build-a-question' })
				}
			}),
		])
		renderCurrentQuestionInto(this.highlighted, (question) => {
			let text = questionToText(question)
			this.abortCurrentParsing()
			if (this.searchInput.value != text) {
				// we update the textarea with execommand so it won't destroy the undo/redo history in the browser
				let originalFocus = document.activeElement
				if (
					!(
						originalFocus.className.includes('selectivity') || originalFocus.tagName == 'SELECT' || !questionEditing.valueOf()
					)
				) {
					// we don't want to restore focus to these elements since it will cause it to popup
					this.searchInput.setSelectionRange(0, this.searchInput.value.length)
					this.injectedInput = true
					this.searchInput.focus()
					document.execCommand('insertText', false, text)
					if (originalFocus) originalFocus.focus()
					this.injectedInput = false
				}
				this.searchInput.value = text
			}
			this.lastQuery = text
			this.searchInput.adjustHeight()
			this.annotations = question.annotations
			let placeholder = this.findPlaceholderPosition()
			if (placeholder) {
				this.searchInput.setSelectionRange(placeholder.start, placeholder.end)
				this.searchInput.focus()
			} else if (text.endsWith('?')) {
				this.searchInput.setSelectionRange(text.length - 1, text.length - 1)
			}
			if (this.searchOnRender) {
				this.searchOnRender = false
				if (!placeholder) {
					this.doSearch()
				}
			}
			return hasFocus
		})
	}
	abortCurrentParsing() {
		if (this.currentParsing?.abort) this.currentParsing?.abort()
		this.currentParsing = null
	}
	inputChange() {
		this.classList.remove('has-searched')
		liveQuestion.valueOf().position = this.searchInput.selectionStart
		this.abortCurrentParsing()
		showSuggestions.put(true)
		suggestionDropdown.resetCursor()
		let parseTimeout, parsingPromise
		this.currentParsing = new Promise((resolve) => {
			parseTimeout = setTimeout(() => {
				parsingPromise = this.parseQuestion()
				resolve(parsingPromise.then ? parsingPromise.catch((error) => {}) : parsingPromise)
				//suggestionDropdown.updateSuggestions()
			}, 200)
		})
		this.currentParsing.abort = () => {
			if (parsingPromise && parsingPromise.abort) parsingPromise.abort()
			else clearTimeout(parseTimeout)
		}

		if (this.queuedChange) {
			clearTimeout(this.queuedChange)
		}
		//					this.queuedChange = setTimeout(() => this.searchInput.dispatchEvent(new InputEvent('change')), 200)
		this.updateAnnotations()
	}
	selectAndShowSuggestions(wasRange) {
		liveQuestion.valueOf().position = this.searchInput.selectionStart
		if (!wasRange) {
			let annotation = this.findAnnotationPosition(this.searchInput.selectionStart)
			if (annotation) {
				this.searchInput.setSelectionRange(annotation.start, annotation.end)
			}
		}
		this.inputChange()
	}
	findPlaceholderPosition() {
		let lastIndex = 0
		for (let annotation of this.annotations || []) {
			let start = lastIndex
			let text = annotation.label || annotation.type || annotation
			let end = start + text.length
			if (annotation.placeholder)
				return {
					start,
					end,
				}
			lastIndex = end
		}
	}

	findAnnotationPosition(position, findNext?) {
		let lastIndex = 0
		let foundPosition
		for (let annotation of this.annotations || []) {
			let start = lastIndex
			let text = annotation.label || annotation.type || annotation
			let end = start + text.length
			if ((start <= position && end >= position) || foundPosition) {
				if (findNext && !foundPosition) foundPosition = true
				else if (typeof annotation == 'object') {
					if (text.charAt(0) == ' ') start++
					if (text.charAt(text.length - 1) == ' ') end--
					return {
						start,
						end,
					}
				}
			}
			lastIndex = end
		}
	}
	updateAnnotations() {
		let query = this.searchInput.value
		this.lastQuery = this.lastQuery || ''
		if (!this.annotations || this.annotations.length == 0) {
			this.annotations = [query]
			return this.setAnnotations()
		}
		let editPoint = query.length,
			insertionLength = 0,
			deletionLength = 0
		let commonEnd = 0,
			i = 0
		for (; i < query.length; i++) {
			if (!this.lastQuery.startsWith(query.slice(0, i + 1))) {
				editPoint = i
				break
			}
		}
		// determine how much inserted text
		for (i = editPoint; i < query.length; i++) {
			if (this.lastQuery.endsWith(query.slice(i))) {
				break
			}
		}
		insertionLength = i - editPoint
		deletionLength = this.lastQuery.length - query.length + insertionLength
		if (insertionLength == 0 && deletionLength == 0) return
		let markedUpQuery = []
		let oldIndex = 0,
			newIndex = 0,
			foundInsertion
		let lastDeletetionType
		let lastAnnotations = this.annotations
		this.annotations = cloneDeep(lastAnnotations)
		const checkPermissions = () => {
			const cursorPosition = this.searchInput.selectionStart
			if (questionEditing.valueOf())
				return true
			if (!hasShownEditingWarning) {
				hasShownEditingWarning = true
				// TODO: Replace with new tour navigation
				// startExplanations(7, true, false, {}, this.searchInput)
			}
			this.searchInput.value = this.lastQuery
			this.annotations = lastAnnotations
			this.searchInput.selectionStart = cursorPosition
			this.searchInput.selectionEnd = cursorPosition
		}
		for (let i = 0; i < this.annotations.length; i++) {
			let annotation = this.annotations[i]
			let oldStart = oldIndex
			let newStart = newIndex
			let oldText = annotation.label || annotation.type || annotation
			let oldEnd = oldStart + oldText.length
			let newEnd
			if (
				(oldEnd > editPoint ||
					(oldEnd == editPoint && annotation.type) ||
					i == this.annotations.length - 1) &&
				!foundInsertion
			) {
				newEnd = Math.max(oldEnd - deletionLength, editPoint) + insertionLength
				if (newEnd - oldEnd > 3) {
					annotation = '' // breakout of the annotation type if we are dramatically increasing the size
				}
				foundInsertion = true
			} else newEnd = newStart + oldText.length
			let newText = query.slice(newStart, newEnd)
			if (newText == '') {
				if (!checkPermissions()) {
					return
				}
				this.annotations.splice(i--, 1)
				lastDeletetionType = annotation.type || 'string'
			} else if (newText != oldText) {
				if (typeof annotation == 'string') {
					if (!checkPermissions()) {
						return
					}
					this.annotations[i] = newText
				} else {
					annotation.label = newText
					annotation.id = annotation.ids = annotation.contains = undefined
					if (lastDeletetionType && annotation.type != lastDeletetionType)
						annotation.tentative = true
				}
			}
			if (newText) lastDeletetionType = null
			oldIndex = oldEnd
			newIndex = newEnd
		}
		this.setAnnotations()
	}
	setAnnotations(annotations?) {
		this.annotations = annotations || this.annotations || []
		this.highlighted.innerHTML = ''
		this.highlighted.append(renderQuestion(this.annotations))
		this.lastQuery = this.searchInput.value
	}
	doAnnotation() {
		let query = this.searchInput.value
		let firstWord = query.match(/\w*/)[0].toLowerCase()
		this.asQuestion = query
		liveQuestion.put(this.asQuestion)
		liveStructuredQuery.then(({ annotations }) => {
			this.annotations = annotations
		})
	}
	clearQuestionBar() {
		liveStructuredQuery.put({ annotations: []})
	}
	parseQuestion() {
		let question = this.searchInput.value
		let position = this.searchInput.selectionStart
		if (question.length < 6) {
			// see if it matches any question templates first
			let questionSuggestions = questionTemplates
				.filter((questionTemplate) =>
					questionTemplate[0].toLowerCase().startsWith(question.toLowerCase().replace(/\?/, ''))
				)
				.map((annotations) => ({ annotations }))
			if (questionSuggestions.length > 0) {
				suggestions.put(_.cloneDeep(questionSuggestions))
				return {
					annotations: [],
					suggestions: suggestions,
					position: 0,
					text: question,
				}
			}
		}
		let currentParsing = this.currentParsing
		let parsedPromise = parseQuestion(this.annotations, position)
		parsedPromise.then(
			(parsed) => {
				if (currentParsing == this.currentParsing) {
					this.currentParsing.abort = null
					parsed.position = position
					parsed.text = question
					let newSuggestions = parsed.suggestions
					if (
						!newSuggestions.some(
							(suggestion) =>
								questionToText(suggestion).toLowerCase().replace(/\?/, '') ==
								questionToText(parsed).toLowerCase().replace(/\?/, '')
						)
					)
						newSuggestions.push({ annotations: parsed.annotations })
					suggestions.put(newSuggestions)
					this.setAnnotations(parsed.annotations)
				}
			},
			(error) => {
				if (!error.isCanceled) console.error(error)
			}
		)
		return parsedPromise
	}
	updateSearch() {
		let searchParameter = location.search.match(/search=([^&]+)/)
		this.searchInput.value = decodeLabel(searchParameter ? searchParameter[1] : '')
		this.searchInput.adjustHeight()
		this.newSearch()
	}
	getArticles(subjectURIs, evidenceResponse) {
		return post('/portal/articles/search', {
			data: {
				queryURIs: subjectURIs,
			},
		}).then(async (searchResults) => {
			return searchResults
		})
	}
	getCurrentQuestion() {
		let currentQuestion = {
			text: this.searchInput.value,
			position: this.searchInput.selectionStart,
			annotations: this.annotations,
		}
		return currentQuestion
	}

	newSearch() {
		if (this.currentParsing) {
			this.searchOnRender = true
			liveParsed.put(this.currentParsing)
			return
		}
		liveQuestion.put(this.getCurrentQuestion())
		this.doSearch()
	}
	async doSearch() {
		this.classList.add('has-searched')
		showSuggestions.put(false)
		executeSearch()
		return
	}
}

const fixCase = (word) => word[0].toUpperCase() + word.slice(1)
const questionWords = ['will', 'how', 'does', 'should', 'would', 'is']

const questionToText = question =>
	question.annotations.map(annotation => annotation.label || annotation.type || annotation).join('')

const interventionsSlot = {
	type: 'intervention',
	placeholder: true
}
const outcomeSlot = {
	type: 'outcome',
	placeholder: true
}
const conditionSlot = {
	type: 'condition',
	placeholder: true
}
const questionTemplates = [
	['How does ', interventionsSlot, ' (vs control) affect outcomes?'],
	['What are treatments for ', conditionSlot, '?'],
	['How does ', interventionsSlot, ' (vs control) affect outcomes for patients with ', conditionSlot, '?'],
	['How does ', interventionsSlot, ' or ', interventionsSlot, ' (vs control) affect outcomes for patients with ', conditionSlot, '?'],
	['How does ', interventionsSlot, ' (vs control) affect ', outcomeSlot, '?'],
	['How does ', interventionsSlot, ' or ', interventionsSlot, ' (vs control) affect ', outcomeSlot, '?'],
	['How does ', interventionsSlot, ' (vs control) affect ', outcomeSlot, ' for patients with ', conditionSlot, '?'],
]
